import { useMemo } from 'react';

/**
 * Compute an itemized quote including taxes and discounts
 *
 * @param {Object} input Information needed to calculate a quote
 * @returns {Object} Computed result containing total, subtotal, discount and taxes
 */
const useQuote = ({
  taxRates,
  percentOff = 0,
  billingInfo: { addressState },
  price: { amount: priceAmount = 0 },
}) => {
  const subtotal = priceAmount / 100;

  const discount = (percentOff / 100) * subtotal;

  const finalSubtotal = subtotal - discount;

  const taxes = useMemo(() => {
    if (addressState === '') {
      return [];
    }

    return taxRates
      .filter(({ jurisdiction }) => jurisdiction === addressState)
      .map(taxRate => ({
        ...taxRate,
        amount: finalSubtotal * (taxRate.percentage / 100),
      }));
  }, [addressState, taxRates, finalSubtotal]);

  const total = useMemo(() => {
    const totalTaxRate = taxes.reduce(
      (totalPercent, { percentage }) => totalPercent + percentage,
      0
    );

    return finalSubtotal * (1 + totalTaxRate / 100);
  }, [finalSubtotal, taxes]);

  return {
    total,
    subtotal,
    taxes,
    discount,
  };
};

export default useQuote;
